import React, { useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { ArrowRight, AlertTriangle, Search, Wrench, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import PageSEO from '@/components/PageSEO';
import { ProblemPageLinks } from '@/components/InternalLinks';

const PROBLEM_PAGES = {
  '/ac-blowing-warm-air': {
    h1: 'AC Blowing Warm Air? Here\'s What to Check',
    intro: 'When your air conditioner is running but blowing warm or hot air, it can be uncomfortable and frustrating — especially during a Louisiana summer. Here\'s what might be going on and what you can do about it.',
    causes: [
      { title: 'Dirty Air Filter', desc: 'A clogged filter restricts airflow, causing the evaporator coil to freeze and the system to blow warm air once it thaws.' },
      { title: 'Low Refrigerant', desc: 'A refrigerant leak means the system can\'t absorb heat properly. The air coming from your vents will feel lukewarm or warm.' },
      { title: 'Thermostat Misconfiguration', desc: 'The thermostat may be set to "fan only" mode instead of "cool," circulating air without actually cooling it.' },
      { title: 'Outdoor Unit Issues', desc: 'The condenser outside may be blocked by debris, or the compressor may have failed, preventing heat from being expelled.' },
      { title: 'Duct Leaks', desc: 'Holes or disconnections in your ductwork can let unconditioned attic air mix with cooled air before it reaches your vents.' },
    ],
    checks: [
      'Check your thermostat — make sure it\'s set to "cool" and the fan is on "auto."',
      'Inspect the air filter and replace it if it looks dirty or clogged.',
      'Go outside and check if the condenser unit is running and clear of debris.',
      'Check your circuit breaker panel — the outdoor unit may have tripped a breaker.',
      'Feel the copper lines going to the outdoor unit. The larger one should feel cold. If it\'s warm, there may be a refrigerant issue.',
    ],
    replacement: 'If your system is over 10 years old and requires a refrigerant recharge (especially R-22), or if the compressor has failed, replacement is often more cost-effective than repair. Compressor replacement alone can cost $1,500–$2,500, which is a significant portion of a new system\'s price.',
  },
  '/ac-freezing-up': {
    h1: 'AC Freezing Up? Causes and Solutions',
    intro: 'Finding ice on your air conditioner\'s coils or refrigerant lines is a clear sign something is wrong. In Louisiana\'s humid climate, a frozen AC can quickly lead to water damage and system failure if left unchecked.',
    causes: [
      { title: 'Restricted Airflow', desc: 'A dirty filter, closed vents, or blocked return air grilles prevent warm air from reaching the evaporator coil, causing it to drop below freezing.' },
      { title: 'Low Refrigerant', desc: 'When refrigerant is low due to a leak, the remaining refrigerant expands too much in the evaporator, causing ice formation.' },
      { title: 'Dirty Evaporator Coil', desc: 'A layer of dust or grime on the evaporator coil acts as insulation, preventing proper heat absorption and leading to freeze-ups.' },
      { title: 'Blower Fan Failure', desc: 'If the blower motor or fan isn\'t running at full speed, there isn\'t enough air moving across the coil to keep it warm.' },
      { title: 'Running AC in Cool Weather', desc: 'Operating your AC when outdoor temperatures are below 60°F can cause the coil to freeze due to insufficient heat load.' },
    ],
    checks: [
      'Turn off the AC immediately and switch the fan to "on" to help thaw the coils.',
      'Check and replace the air filter if it\'s dirty.',
      'Make sure all supply and return vents are open and unblocked by furniture or curtains.',
      'Once thawed (1–3 hours), turn the AC back on and monitor for re-freezing.',
      'If the system freezes again within 24 hours, turn it off and call a professional — you likely have a refrigerant leak or mechanical issue.',
    ],
    replacement: 'Repeated freeze-ups can permanently damage your compressor, which is the most expensive component in your system. If your unit is over 10 years old and experiencing frequent freeze-ups despite maintenance, it may be more economical to replace the entire system rather than continue repairing.',
  },
  '/ac-running-but-not-cooling': {
    h1: 'AC Running But Not Cooling? What\'s Going On',
    intro: 'Your AC is running, the thermostat says it\'s working, but your house just won\'t cool down. This is one of the most common HVAC complaints in Louisiana, especially during peak summer when systems are pushed to their limits.',
    causes: [
      { title: 'Undersized System', desc: 'Your AC may not have enough capacity (BTUs) for your home\'s square footage, especially if you\'ve added rooms or have poor insulation.' },
      { title: 'Refrigerant Leak', desc: 'Low refrigerant reduces cooling capacity. The system runs constantly but can\'t remove enough heat from the air.' },
      { title: 'Dirty Condenser Coils', desc: 'The outdoor unit\'s coils release heat. When covered in dirt, pollen, or grass clippings, they can\'t expel heat efficiently.' },
      { title: 'Failing Compressor', desc: 'The compressor is the heart of the system. If it\'s weakening, the system may run but produce insufficient cooling.' },
      { title: 'Duct Leaks', desc: 'Up to 30% of cooled air can be lost through leaky ductwork, especially in unconditioned attic spaces common in Louisiana homes.' },
    ],
    checks: [
      'Verify the thermostat is set at least 3–5 degrees below the current room temperature.',
      'Replace the air filter if it hasn\'t been changed in the last 30 days.',
      'Spray the outdoor condenser unit gently with a garden hose to remove dirt and debris.',
      'Check that all vents are open and that air is flowing from each one.',
      'Feel the air coming from your closest vent — if it\'s cool but weak, the issue is likely airflow (ducts or blower). If the air isn\'t cool at all, it\'s a refrigerant or compressor problem.',
    ],
    replacement: 'If your system runs continuously but can\'t maintain your set temperature — especially if it\'s over 12 years old — it may have lost too much efficiency to be worth repairing. Modern systems with higher SEER ratings can cool your home while using 30–50% less energy.',
  },
  '/ac-leaking-water': {
    h1: 'AC Leaking Water? Here\'s What to Do',
    intro: 'Water pooling around your indoor AC unit or dripping from vents is never normal. In Louisiana\'s high-humidity environment, your AC removes gallons of moisture from the air daily — and when something goes wrong with drainage, it shows up fast.',
    causes: [
      { title: 'Clogged Condensate Drain Line', desc: 'The most common cause. Algae, mold, and debris build up in the PVC drain line, blocking water from draining properly.' },
      { title: 'Rusted or Cracked Drain Pan', desc: 'The pan that catches condensation under the evaporator coil can rust through or crack, especially in older units.' },
      { title: 'Dirty Air Filter', desc: 'A clogged filter restricts airflow, causing the evaporator coil to freeze. When it thaws, excess water overflows the drain pan.' },
      { title: 'Low Refrigerant', desc: 'Similar to a dirty filter, low refrigerant causes the coil to freeze and produce excess water during thaw cycles.' },
      { title: 'Disconnected Drain Line', desc: 'Vibration or improper installation can cause the drain line to come loose from the unit, allowing water to leak directly.' },
    ],
    checks: [
      'Turn off the AC to prevent water damage and check the area around the indoor unit.',
      'Locate the condensate drain line (usually a PVC pipe near the outdoor unit) and check if water is flowing out.',
      'If no water is coming out, the drain line is likely clogged. You can try flushing it with a mixture of warm water and vinegar.',
      'Check and replace your air filter.',
      'Inspect the drain pan under the evaporator coil (inside the air handler) for cracks or rust.',
    ],
    replacement: 'Water leaks themselves don\'t always mean you need a new system. However, if the leaks are caused by a corroded evaporator coil or if your unit is old enough that the drain pan and coils are deteriorating, it may be time to consider replacement — especially if repairs would exceed $1,000 on a 10+ year-old system.',
  },
  '/ac-not-turning-on': {
    h1: 'AC Not Turning On? Troubleshooting Steps',
    intro: 'When your AC won\'t start at all — no sounds, no airflow, nothing — it\'s alarming, especially in the Louisiana heat. The good news is that many causes are simple and can be diagnosed before calling a technician.',
    causes: [
      { title: 'Tripped Circuit Breaker', desc: 'Power surges, storms, and electrical issues can trip the breaker for your AC unit. This is the most common and easiest fix.' },
      { title: 'Thermostat Issues', desc: 'Dead batteries, incorrect settings, or a malfunctioning thermostat can prevent the signal from reaching your AC unit.' },
      { title: 'Blown Fuse or Capacitor', desc: 'The start/run capacitor gives the compressor the electrical boost it needs to start. A failed capacitor means the unit can\'t turn on.' },
      { title: 'Safety Switch Triggered', desc: 'Many units have a float switch that shuts off the system if the drain pan is full, preventing water damage.' },
      { title: 'Compressor or Motor Failure', desc: 'If the compressor or blower motor has burned out, the system won\'t start. You may hear a humming sound or a click followed by nothing.' },
    ],
    checks: [
      'Check your thermostat — replace batteries if needed, and make sure it\'s set to "cool" with a temperature below room temp.',
      'Go to your electrical panel and check if the AC breaker has tripped. Reset it by flipping it fully off, then back on.',
      'Check for a power disconnect switch near the outdoor unit (usually a gray box on the wall). Make sure it\'s in the "on" position.',
      'Look at the drain pan near the indoor unit — if it\'s full of water, the float switch may have shut down the system.',
      'If the breaker trips again immediately after reset, do NOT keep resetting it — this indicates an electrical fault that needs professional diagnosis.',
    ],
    replacement: 'If the issue is a failed compressor or burned-out motor in a system that\'s 10+ years old, replacement costs for these parts can run $1,000–$3,000. At that point, investing in a new, energy-efficient system with a full warranty is usually the better financial decision.',
  },
  '/ac-making-loud-noise': {
    h1: 'AC Making Loud Noise? What Each Sound Means',
    intro: 'Strange sounds from your air conditioner aren\'t just annoying — they\'re your system telling you something is wrong. Different noises point to different problems, and catching them early can prevent expensive damage.',
    causes: [
      { title: 'Banging or Clanking', desc: 'Usually indicates a loose or broken part inside the compressor, or a disconnected blower fan blade hitting other components.' },
      { title: 'Screeching or Squealing', desc: 'Often caused by a worn fan belt or failing blower motor bearings. In newer belt-less systems, it may indicate motor bearing failure.' },
      { title: 'Buzzing', desc: 'Can indicate loose parts, a failing outdoor fan motor, debris in the unit, or electrical issues like a malfunctioning contactor.' },
      { title: 'Hissing', desc: 'A hissing sound from the indoor unit often indicates a refrigerant leak. From the outdoor unit, it could be a high-pressure buildup.' },
      { title: 'Clicking', desc: 'Constant clicking at startup often points to a failing capacitor or relay. Clicking from the thermostat area may indicate a wiring issue.' },
      { title: 'Rattling', desc: 'Usually caused by loose screws, debris in the condenser, or loose ductwork connections. Often a simple fix.' },
    ],
    checks: [
      'Turn off the system immediately if you hear banging, screeching, or hissing — these can indicate serious mechanical problems.',
      'For buzzing or rattling: check the outdoor unit for visible debris (sticks, leaves) and remove them carefully.',
      'Tighten any visible loose screws on access panels, vent covers, and the outdoor unit housing.',
      'If the noise is coming from inside the ducts, it may be expansion/contraction (normal) or loose duct connections.',
      'For clicking sounds: check if the system is trying to start but failing. If so, it\'s likely a capacitor issue that needs professional repair.',
    ],
    replacement: 'If your system is making loud banging or screeching noises and the compressor is the source, repair costs can approach 50% of a new system\'s price. For systems over 10 years old with compressor, motor, or multiple component failures, replacement is typically recommended.',
  },
};

const AcProblemPage = () => {
  const location = useLocation();
  const data = PROBLEM_PAGES[location.pathname];

  const relatedProblems = useMemo(() => {
    if (!data) return [];
    return Object.entries(PROBLEM_PAGES)
      .filter(([path]) => path !== location.pathname)
      .slice(0, 3);
  }, [location.pathname, data]);

  if (!data) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <PageSEO breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'Common AC Problems', url: '/common-ac-problems' },
        { name: data.h1.split('?')[0].trim(), url: location.pathname },
      ]} />
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="problem-page-heading"
          >
            {data.h1}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            {data.intro}
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get an Instant Replacement Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Possible Causes */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <AlertTriangle className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Possible Causes
            </h2>
          </div>
          <div className="space-y-4">
            {data.causes.map((cause, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6" data-testid={`cause-${i}`}>
                <h3 className="font-bold text-[#0F172A] mb-1">{cause.title}</h3>
                <p className="text-slate-600 text-sm">{cause.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Homeowner Checks */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <Search className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What You Can Check Yourself
            </h2>
          </div>
          <div className="space-y-3">
            {data.checks.map((check, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl" data-testid={`check-${i}`}>
                <span className="w-7 h-7 bg-[#0EA5E9] text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                  {i + 1}
                </span>
                <p className="text-[#0F172A] text-sm">{check}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* When Replacement Is Necessary */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <RefreshCw className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              When Replacement May Be Necessary
            </h2>
          </div>
          <div className="bg-[#FFF7ED] border border-orange-200 rounded-2xl p-8">
            <p className="text-slate-700 leading-relaxed">{data.replacement}</p>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-slate max-w-none">
            <p>
              Want to understand your options? Get a free{' '}
              <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9]">instant HVAC estimate</a>{' '}
              in minutes — no phone call required. You can also check{' '}
              <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement costs in New Orleans</a>{' '}
              or browse{' '}
              <a href="/hvac-pricing-new-orleans" className="text-[#0EA5E9]">HVAC pricing</a>{' '}
              for your area.
            </p>
            <p>
              Not sure how old your system is?{' '}
              <a href="/check-ac-age" className="text-[#0EA5E9]">Check your AC unit's age</a>{' '}
              or learn about{' '}
              <a href="/repair-vs-replace-ac" className="text-[#0EA5E9]">when to repair vs. replace</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Related Problems */}
      <section className="py-16 bg-slate-50" data-testid="related-problems-section">
        <div className="max-w-4xl mx-auto px-6">
          <h2
            className="text-2xl font-bold text-[#0F172A] mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Other Common AC Problems
          </h2>
          <p className="text-slate-500 mb-8">Explore other issues Louisiana homeowners deal with.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedProblems.map(([path, page]) => (
                <a
                  key={path}
                  href={path}
                  data-testid={`related-problem-${path.slice(1)}`}
                  className="group block p-5 bg-white rounded-xl border border-slate-200 hover:border-[#0EA5E9] hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="w-4 h-4 text-[#F97316]" />
                    <h3 className="font-bold text-sm text-[#0F172A] group-hover:text-[#0EA5E9] transition-colors">
                      {page.h1.replace(/\?.*$/, '').replace(/Here's.*$/, '').replace(/What.*$/, '').replace(/Causes.*$/, '').replace(/Troubleshooting.*$/, '').trim()}
                    </h3>
                  </div>
                  <p className="text-slate-500 text-xs line-clamp-2">{page.intro.substring(0, 100)}...</p>
                </a>
              ))}
          </div>
        </div>
      </section>

      <ProblemPageLinks />

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Need a New System? Get an Instant Estimate
          </h2>
          <p className="text-slate-300 mb-8">
            See what a replacement would cost for your home — free, fast, and no obligation.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AcProblemPage;
